import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Modal, ModalHeader, ModalBody, Button, Input, Spinner, Badge, Progress
} from "reactstrap";
import { toast } from "react-toastify";
import IntegrationService from "../../../services/integration_service";

/**
 * NotifyStudentsModal
 * Modal para composição e envio de mensagens WhatsApp (Evolution API)
 * para os alunos da sessão atual. Suporta texto + imagem opcional.
 * Envio throttled (3s entre mensagens) para evitar bloqueio.
 */
const SEND_DELAY_MS = 3000;

const NotifyStudentsModal = ({ isOpen, toggle, members, idTenant }) => {
    const [message, setMessage] = useState("");
    const [imagePreview, setImagePreview] = useState(null);
    const [imageBase64, setImageBase64] = useState(null);
    const [evolutionConfig, setEvolutionConfig] = useState(null);
    const [evolutionOnline, setEvolutionOnline] = useState(false);
    const [checkingEvolution, setCheckingEvolution] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendProgress, setSendProgress] = useState({ current: 0, total: 0, results: [] });
    const [selectedIds, setSelectedIds] = useState(new Set());
    const cancelRef = useRef(false);

    // Membros que têm telefone
    const membersWithPhone = (members || []).filter(m => {
        const phone = (m.phone || "").replace(/\D/g, "");
        return phone.length >= 10;
    });

    // Membros selecionados para envio
    const selectedMembers = membersWithPhone.filter(m => selectedIds.has(m.id));

    // Inicializar todos selecionados quando a lista muda
    useEffect(() => {
        if (membersWithPhone.length > 0) {
            setSelectedIds(new Set(membersWithPhone.map(m => m.id)));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [members, isOpen]);

    const toggleMember = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        if (selectedIds.size === membersWithPhone.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(membersWithPhone.map(m => m.id)));
        }
    };

    // Carregar e verificar Evolution API
    useEffect(() => {
        if (!isOpen || !idTenant) return;
        cancelRef.current = false;

        const loadEvolution = async () => {
            setCheckingEvolution(true);
            try {
                const integration = await IntegrationService.getByProvider(idTenant, "evolution");
                if (integration && integration.isActive && integration.config?.apiKey) {
                    setEvolutionConfig(integration.config);
                    const status = await IntegrationService.checkEvolutionStatus(integration.config);
                    setEvolutionOnline(status.ok && status.state === "open");
                } else {
                    setEvolutionConfig(null);
                    setEvolutionOnline(false);
                }
            } catch (err) {
                console.error("[NotifyStudentsModal] Erro ao verificar Evolution:", err);
                setEvolutionConfig(null);
                setEvolutionOnline(false);
            } finally {
                setCheckingEvolution(false);
            }
        };

        loadEvolution();
    }, [isOpen, idTenant]);

    // Reset ao fechar
    useEffect(() => {
        if (!isOpen) {
            setMessage("");
            setImagePreview(null);
            setImageBase64(null);
            setSending(false);
            setSendProgress({ current: 0, total: 0, results: [] });
            cancelRef.current = true;
        }
    }, [isOpen]);

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            toast.warning("A imagem deve ter no máximo 5MB.");
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setImageBase64(reader.result);
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const removeImage = () => {
        setImagePreview(null);
        setImageBase64(null);
    };

    const delay = (ms) => new Promise(r => setTimeout(r, ms));

    const handleSend = useCallback(async () => {
        if (!message.trim() && !imageBase64) {
            toast.warning("Escreva uma mensagem ou anexe uma imagem.");
            return;
        }
        if (!evolutionConfig || !evolutionOnline) {
            toast.error("Evolution API não está conectada.");
            return;
        }
        if (selectedMembers.length === 0) {
            toast.warning("Selecione pelo menos um aluno.");
            return;
        }

        setSending(true);
        cancelRef.current = false;
        const total = selectedMembers.length;
        const results = [];
        setSendProgress({ current: 0, total, results: [] });

        for (let i = 0; i < total; i++) {
            if (cancelRef.current) break;

            const member = selectedMembers[i];
            try {
                let result;
                if (imageBase64) {
                    // Envia imagem com legenda
                    result = await IntegrationService.sendEvolutionMedia(
                        evolutionConfig, member.phone, imageBase64, message.trim()
                    );
                } else {
                    // Envia só texto
                    result = await IntegrationService.sendEvolutionMessage(
                        evolutionConfig, member.phone, message.trim()
                    );
                }
                results.push({ name: member.name, success: result.success, reason: result.reason });
            } catch (err) {
                results.push({ name: member.name, success: false, reason: err.message });
            }

            setSendProgress({ current: i + 1, total, results: [...results] });

            // Throttling: espera entre envios (exceto o último)
            if (i < total - 1 && !cancelRef.current) {
                await delay(SEND_DELAY_MS);
            }
        }

        setSending(false);

        const successCount = results.filter(r => r.success).length;
        const failCount = results.filter(r => !r.success).length;

        if (failCount === 0) {
            toast.success(`✅ ${successCount} mensagen${successCount > 1 ? 's' : ''} enviada${successCount > 1 ? 's' : ''} com sucesso!`);
        } else {
            toast.warning(`${successCount} enviada${successCount > 1 ? 's' : ''}, ${failCount} falha${failCount > 1 ? 's' : ''}.`);
        }
    }, [message, imageBase64, evolutionConfig, evolutionOnline, selectedMembers]);

    const handleCancel = () => {
        cancelRef.current = true;
        setSending(false);
    };

    const isDone = sendProgress.current > 0 && sendProgress.current === sendProgress.total && !sending;

    return (
        <Modal isOpen={isOpen} toggle={sending ? undefined : toggle} size="lg" centered contentClassName="border-0 shadow-lg">
            <ModalHeader toggle={sending ? undefined : toggle} className="border-bottom bg-light pb-3">
                <div className="d-flex align-items-center gap-2">
                    <i className="mdi mdi-whatsapp text-success font-size-22"></i>
                    <div>
                        <h6 className="mb-0 fw-bold">Notificar Alunos</h6>
                        <small className="text-muted">Enviar mensagem via WhatsApp</small>
                    </div>
                </div>
            </ModalHeader>

            <ModalBody className="p-4">
                {/* Status da Evolution */}
                {checkingEvolution ? (
                    <div className="text-center py-3">
                        <Spinner size="sm" color="primary" className="me-2" />
                        <span className="text-muted">Verificando conexão WhatsApp...</span>
                    </div>
                ) : !evolutionConfig ? (
                    <div className="alert alert-warning border-0 shadow-sm text-center mb-0">
                        <i className="mdi mdi-alert-circle-outline font-size-20 d-block mb-2"></i>
                        <strong>Evolution API não configurada.</strong>
                        <p className="mb-0 small text-muted mt-1">Configure a integração em Configurações → Integrações.</p>
                    </div>
                ) : !evolutionOnline ? (
                    <div className="alert alert-danger border-0 shadow-sm text-center mb-0">
                        <i className="mdi mdi-wifi-off font-size-20 d-block mb-2"></i>
                        <strong>WhatsApp desconectado.</strong>
                        <p className="mb-0 small text-muted mt-1">Escaneie o QR Code na tela de integrações para reconectar.</p>
                    </div>
                ) : (
                    <>
                        {/* Status online */}
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <span className="bg-success rounded-circle" style={{ width: 8, height: 8, display: 'inline-block' }}></span>
                            <small className="text-success fw-bold">WhatsApp conectado</small>
                            <span className="ms-auto">
                                <Badge color="primary" className="rounded-pill px-3">
                                    {selectedMembers.length} de {membersWithPhone.length} selecionado{selectedMembers.length !== 1 ? 's' : ''}
                                </Badge>
                                {members.length - membersWithPhone.length > 0 && (
                                    <Badge color="warning" className="rounded-pill px-2 ms-1" title="Alunos sem telefone cadastrado">
                                        {members.length - membersWithPhone.length} sem tel.
                                    </Badge>
                                )}
                            </span>
                        </div>

                        {/* Composição da mensagem */}
                        {!isDone && (
                            <>
                                <div className="mb-3">
                                    <label className="form-label fw-bold small text-uppercase text-muted">Mensagem</label>
                                    <Input
                                        type="textarea"
                                        rows={4}
                                        placeholder="Digite a mensagem para os alunos..."
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        disabled={sending}
                                        className="shadow-sm"
                                        style={{ resize: 'vertical' }}
                                    />
                                </div>

                                {/* Upload de imagem */}
                                <div className="mb-3">
                                    <label className="form-label fw-bold small text-uppercase text-muted">Imagem (opcional)</label>
                                    {imagePreview ? (
                                        <div className="position-relative d-inline-block">
                                            <img
                                                src={imagePreview}
                                                alt="Preview"
                                                style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.1)', objectFit: 'cover' }}
                                            />
                                            {!sending && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger position-absolute top-0 end-0 rounded-circle shadow"
                                                    style={{ width: '24px', height: '24px', padding: 0, transform: 'translate(30%, -30%)' }}
                                                    onClick={removeImage}
                                                >
                                                    <i className="mdi mdi-close" style={{ fontSize: '12px' }}></i>
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <label
                                            className="d-flex align-items-center gap-2 border border-dashed rounded-3 p-3 text-muted"
                                            style={{ cursor: sending ? 'default' : 'pointer', background: '#fafbfc' }}
                                        >
                                            <i className="mdi mdi-image-plus font-size-22"></i>
                                            <span className="small">Clique para anexar uma imagem (máx. 5MB)</span>
                                            <Input
                                                type="file"
                                                accept="image/*"
                                                className="d-none"
                                                onChange={handleImageUpload}
                                                disabled={sending}
                                            />
                                        </label>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Progresso de envio */}
                        {(sending || isDone) && (
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <small className="fw-bold text-muted">
                                        {sending ? "Enviando..." : "Envio concluído"}
                                    </small>
                                    <small className="fw-bold">
                                        {sendProgress.current}/{sendProgress.total}
                                    </small>
                                </div>
                                <Progress
                                    value={(sendProgress.current / sendProgress.total) * 100}
                                    color={isDone ? "success" : "primary"}
                                    animated={sending}
                                    className="shadow-sm"
                                    style={{ height: '8px' }}
                                />

                                {/* Lista de resultados */}
                                <div className="mt-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {sendProgress.results.map((r, i) => (
                                        <div key={i} className="d-flex align-items-center gap-2 py-1 border-bottom" style={{ fontSize: '13px' }}>
                                            <i className={`mdi ${r.success ? 'mdi-check-circle text-success' : 'mdi-alert-circle text-danger'}`}></i>
                                            <span className="fw-medium">{r.name}</span>
                                            {!r.success && r.reason && (
                                                <small className="text-danger ms-auto">{r.reason}</small>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Destinatários com checkboxes */}
                        {!sending && !isDone && (
                            <div className="mb-3">
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <label className="form-label fw-bold small text-uppercase text-muted mb-0">
                                        <i className="mdi mdi-account-group-outline me-1"></i>
                                        Destinatários
                                    </label>
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 text-primary fw-bold"
                                        style={{ fontSize: '12px' }}
                                        onClick={toggleAll}
                                    >
                                        {selectedIds.size === membersWithPhone.length ? 'Desmarcar todos' : 'Selecionar todos'}
                                    </button>
                                </div>
                                <div className="border rounded-3 p-2" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                    {membersWithPhone.map(m => (
                                        <div
                                            key={m.id}
                                            className="d-flex align-items-center gap-2 py-1 px-1 rounded-2"
                                            style={{ fontSize: '13px', cursor: 'pointer', transition: 'background 0.15s' }}
                                            onClick={() => toggleMember(m.id)}
                                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.03)'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(m.id)}
                                                onChange={() => toggleMember(m.id)}
                                                onClick={e => e.stopPropagation()}
                                                className="form-check-input m-0"
                                                style={{ cursor: 'pointer' }}
                                            />
                                            <i className="mdi mdi-whatsapp text-success"></i>
                                            <span className={`fw-medium ${!selectedIds.has(m.id) ? 'text-muted text-decoration-line-through' : ''}`}>{m.name}</span>
                                            <small className="text-muted ms-auto">{m.phone}</small>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Ações */}
                        <div className="d-flex justify-content-end gap-2 pt-2 border-top">
                            {sending ? (
                                <Button color="danger" outline onClick={handleCancel}>
                                    <i className="mdi mdi-stop-circle-outline me-1"></i> Cancelar Envio
                                </Button>
                            ) : isDone ? (
                                <Button color="primary" onClick={toggle}>
                                    <i className="mdi mdi-check me-1"></i> Fechar
                                </Button>
                            ) : (
                                <>
                                    <Button color="light" onClick={toggle}>Cancelar</Button>
                                    <Button
                                        color="success"
                                        className="shadow-sm fw-bold px-4"
                                        onClick={handleSend}
                                        disabled={(!message.trim() && !imageBase64) || selectedMembers.length === 0}
                                    >
                                        <i className="mdi mdi-send me-1"></i>
                                        Enviar para {selectedMembers.length} aluno{selectedMembers.length !== 1 ? 's' : ''}
                                    </Button>
                                </>
                            )}
                        </div>
                    </>
                )}
            </ModalBody>
        </Modal>
    );
};

export default NotifyStudentsModal;
